/* ═══════════════════════════════════════════════════════════════════════════════
   TourEngine: Generic Tour Orchestrator
   
   Renders the correct screen based on the current tour step:
   - Step 0 (welcome type): TourWelcome
   - Steps 1-N (spotlight type): TourSpotlight + TourTooltip
   - Last step (completion type): TourCompletion
   
   ARCHITECTURE:
   - Spotlight overlay renders at z-index: 10000 (dim + cutout hole)
   - TourTooltip renders in its OWN portal at z-index: 10002
   - For steps with `skipOverlay: true` (targeting elements inside other modals),
     the spotlight is skipped entirely, only the tooltip shows
   - `onEnter` callbacks fire on each step change for side effects
   
   REUSABLE: Just pass a TourDefinition and this handles everything.
   ═══════════════════════════════════════════════════════════════════════════════ */

import { useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence } from 'framer-motion';
import type { TourDefinition } from '../../lib/tours/types';
import { TourWelcome } from './TourWelcome';
import { TourCompletion } from './TourCompletion';
import { TourInfoCard } from './TourInfoCard';
import { TourSpotlight } from './TourSpotlight';
import { TourTooltip } from './TourTooltip';
import { calculateTooltipPosition } from './tourSpotlightUtils';
import type { SpotlightRect } from './tourSpotlightUtils';
import './TourEngine.css';

interface TourEngineProps {
  tour: TourDefinition;
  onComplete: () => void;
  onSkip: () => void;
}

export function TourEngine({ tour, onComplete, onSkip }: TourEngineProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState<SpotlightRect | null>(null);
  const totalSteps = tour.steps.length;
  const step = tour.steps[currentStep];

  // ─── Navigation ───────────────────────────────────────────────────────────
  const next = useCallback(() => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep((s) => s + 1);
    }
  }, [currentStep, totalSteps]);

  const prev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  }, [currentStep]);

  // ─── Keyboard handlers ────────────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onSkip();
          break;
        case 'ArrowRight':
          if (!step.isInteractive) next();
          break;
        case 'ArrowLeft':
          prev();
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [next, prev, onSkip, step]);

  // ─── Step lifecycle: onEnter callback ─────────────────────────────────────
  useEffect(() => {
    step.onEnter?.();
  }, [currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Interactive step listener ────────────────────────────────────────────
  useEffect(() => {
    if (!step.isInteractive || !step.targetSelector) return;

    const eventName = step.interactionEvent || 'click';
    const el = document.querySelector(step.targetSelector);
    if (!el) return;

    const handler = () => {
      // Delay to let triggered UI (e.g., filter modal) mount in the DOM
      setTimeout(next, 600);
    };

    el.addEventListener(eventName, handler, { once: true });
    return () => el.removeEventListener(eventName, handler);
  }, [step, next]);

  // ─── Spotlight rect tracking ──────────────────────────────────────────────
  // Runs for ALL spotlight steps (even skipOverlay) to position the tooltip
  useEffect(() => {
    if (step.type !== 'spotlight' || !step.targetSelector) {
      queueMicrotask(() => setSpotlightRect(null));
      return;
    }

    const measure = () => {
      const el = document.querySelector(step.targetSelector!);
      if (!el) {
        setSpotlightRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      const pad = step.spotlightPadding ?? 12;
      setSpotlightRect({
        x: r.x - pad,
        y: r.y - pad,
        width: r.width + pad * 2,
        height: r.height + pad * 2,
      });
    };

    // Initial delay for scroll/mount + measure
    const timeout = setTimeout(measure, 500);
    const interval = setInterval(measure, 1000);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [step]);

  // NOTE: We intentionally do NOT lock body scroll here.
  // The overlay at z-10000 already prevents user interaction with the page.
  // Locking body scroll prevents scrollIntoView() from working on
  // below-the-fold elements (e.g., pnl-section).

  // ─── Determine if spotlight should render ─────────────────────────────────
  const showSpotlight =
    step.type === 'spotlight' &&
    step.targetSelector &&
    !step.skipOverlay;

  // ─── Render ───────────────────────────────────────────────────────────────
  const content = (
    <div className="tour-engine" role="dialog" aria-modal="true" aria-label={`Tour: ${tour.title}`}>
      <AnimatePresence mode="wait">
        {step.type === 'welcome' && (
          <TourWelcome
            key="welcome"
            tour={tour}
            onNext={next}
            onSkip={onSkip}
          />
        )}

        {step.type === 'completion' && (
          <TourCompletion
            key="completion"
            step={step}
            onComplete={onComplete}
          />
        )}

        {step.type === 'info_card' && (
          <TourInfoCard
            key={`info-${step.id}`}
            step={step}
            currentStep={currentStep}
            totalSteps={totalSteps}
            onNext={next}
            onPrev={prev}
            onSkip={onSkip}
          />
        )}

        {/* Spotlight overlay: only for steps WITHOUT skipOverlay */}
        {showSpotlight && (
          <TourSpotlight
            key={`spotlight-${step.id}`}
            targetSelector={step.targetSelector!}
            padding={step.spotlightPadding}
            isInteractive={step.isInteractive}
          />
        )}
      </AnimatePresence>
    </div>
  );

  // ─── Tooltip Portal ───────────────────────────────────────────────────────
  // Rendered in its own portal at z-index: 10002 to avoid stacking context
  // traps. Shows for ALL spotlight steps (with or without overlay).
  const tooltipPortal =
    step.type === 'spotlight' && step.targetSelector
      ? createPortal(
          <div
            className="tour-tooltip-portal"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 10050,
              pointerEvents: 'none',
            }}
          >
            <div style={{ pointerEvents: 'auto' }}>
              <AnimatePresence mode="wait">
                <TourTooltip
                  key={`tooltip-${step.id}`}
                  step={step}
                  currentStep={currentStep}
                  totalSteps={totalSteps}
                  style={calculateTooltipPosition(
                    spotlightRect,
                    step.position || 'bottom',
                    360,
                    16,
                  )}
                  onNext={next}
                  onPrev={prev}
                  onSkip={onSkip}
                />
              </AnimatePresence>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      {createPortal(content, document.body)}
      {tooltipPortal}
    </>
  );
}
